import { useCallback, useEffect, useState } from 'react'

import { useAuthenticatedRequestor } from '@app/rest/rest.hooks'
import { convertApiEntry } from '@app/state/visits/helpers'
import { retrieveVisitEntryForInstrument } from '@app/state/visits/rest'

import { submitResponses } from '../visit/visit.rest'

import {
  getInstruments as getInstrumentsFromAPI,
  getInstrument,
  getInstrumentsForPatient,
  getInstrumentResponsesForPatient,
} from './instrument.rest'

import type {
  IInstrument,
  IUseInstruments,
  IUseInstrument,
  IEntry,
  IInstrumentMetadata,
  IUsePatientInstruments,
  IUsePatientInstrumentResponses,
} from './instrument.interface'
import type { IPatientId } from '../patient/patient.interface'

export function useInstrument(
  instrumentId: string,
  version?: number,
): IUseInstrument {
  const [instrument, setInstrument] = useState<IInstrument>(undefined)

  const requestor = useAuthenticatedRequestor()

  const fetchInstrument = useCallback(async () => {
    const response = await requestor(getInstrument(instrumentId, version))

    if (response.status === 200) {
      const instrument = response.data.instrument
      setInstrument(instrument)
    } else {
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }
  }, [instrumentId, requestor, version])

  useEffect(() => {
    if (instrumentId) {
      fetchInstrument()
    } else {
      setInstrument(undefined)
    }
  }, [fetchInstrument, instrumentId])

  return {
    instrument,
    getInstrument: fetchInstrument,
  }
}

export function useInstruments(): IUseInstruments {
  const [instruments, setInstruments] = useState<IInstrument[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)
  const requestor = useAuthenticatedRequestor()

  const getInstruments = useCallback(async () => {
    setIsError(false)
    setIsLoading(true)
    const response = await requestor(getInstrumentsFromAPI())

    if (response.status === 200) {
      setInstruments(response.data.instruments)
    } else {
      setIsError(true)
      throw new Error(`server returned a non 200 response '${response.status}'`)
    }

    setIsLoading(false)
  }, [requestor])

  useEffect(() => {
    getInstruments()
  }, [getInstruments])

  return {
    isError,
    isLoading,
    instruments,
    getInstruments,
  }
}

export function useInstrumentsForPatient(
  patientId: string,
): IUsePatientInstruments {
  const [instrumentMetadata, setInstrumentMetadata] = useState<
    IInstrumentMetadata[]
  >([])

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)
  const requestor = useAuthenticatedRequestor()

  const getInstruments = useCallback(async () => {
    setIsError(false)
    setIsLoading(true)
    const response = await requestor(getInstrumentsForPatient(patientId))

    if (response.status === 200) {
      setInstrumentMetadata(response.data.metadata)
    } else {
      setIsError(true)
      throw new Error(
        `Requesting patient instruments failed '${response.status}'`,
      )
    }

    setIsLoading(false)
  }, [patientId, requestor])

  useEffect(() => {
    getInstruments()
  }, [getInstruments])

  return {
    isLoading,
    isError,
    instrumentMetadata,
    getInstruments,
  }
}

export function usePatientInstrumentResponses(
  instrumentId: string,
  instrumentVersion: number,
  patientId: IPatientId,
): IUsePatientInstrumentResponses {
  const [entries, setEntries] = useState<IEntry[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)
  const requestor = useAuthenticatedRequestor()

  const getEntries = useCallback(async () => {
    setIsError(false)
    setIsLoading(true)
    const response = await requestor(
      getInstrumentResponsesForPatient(
        patientId,
        instrumentId,
        instrumentVersion,
      ),
    )

    if (response.status === 200) {
      setEntries(response.data.entries)
    } else {
      setIsError(true)
      throw new Error(
        `Requesting patient instrument responses failed '${response.status}'`,
      )
    }

    setIsLoading(false)
  }, [instrumentId, instrumentVersion, patientId, requestor])

  const editResponses = useCallback(
    async (entryId: string, responses: Record<string, string>) => {
      const response = await requestor(submitResponses(entryId, responses))
      if (response.status === 200) {
        await getEntries()
      } else {
        setIsError(true)
        throw new Error(
          `Could not edit instrument responses. '${response.status}'`,
        )
      }
    },
    [getEntries, requestor],
  )

  useEffect(() => {
    getEntries()
  }, [getEntries])

  return {
    isError,
    isLoading,
    editResponses,
    entries,
    getEntries: getEntries,
  }
}

export function useVisitInstrumentResponses(
  instrumentId: string,
  instrumentVersion: number,
  visitId: string,
): IUsePatientInstrumentResponses {
  const [entries, setEntries] = useState<IEntry[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)
  const requestor = useAuthenticatedRequestor()

  const getEntries = useCallback(async () => {
    setIsError(false)
    setIsLoading(true)
    const response = await requestor(
      retrieveVisitEntryForInstrument(visitId, instrumentId, instrumentVersion),
    )

    if (response.status === 200) {
      const entry = convertApiEntry(response.data.entry)
      setEntries([entry])
    } else {
      setIsError(true)
      throw new Error(
        `Requesting patient instrument responses failed '${response.status}'`,
      )
    }

    setIsLoading(false)
  }, [instrumentId, instrumentVersion, requestor, visitId])

  const editResponses = useCallback(
    async (entryId: string, responses: Record<string, string>) => {
      const response = await requestor(submitResponses(entryId, responses))
      if (response.status === 200) {
        await getEntries()
      } else {
        setIsError(true)
        throw new Error(
          `Could not edit instrument responses. '${response.status}'`,
        )
      }
    },
    [getEntries, requestor],
  )

  useEffect(() => {
    getEntries()
  }, [getEntries])

  return {
    isError,
    isLoading,
    editResponses,
    entries,
    getEntries: getEntries,
  }
}
